import { useEffect, useMemo, useState } from 'react';

import { ConfigProvider, App as AntApp } from 'antd';
import enUS from 'antd/locale/en_US';
import viVN from 'antd/locale/vi_VN';
import { useTranslation } from 'react-i18next';
import { RouterProvider } from 'react-router-dom';

import { ErrorBoundary } from '@/components/errors/ErrorBoundary';
import { PageLoading } from '@/components/loading/PageLoading';
import { env } from '@/config/env';
import { ColorSchemeProvider, useColorScheme } from '@/contexts/ColorSchemeContext';
import { MapSarJobFailureModalHost } from '@/features/analytics/components/MapSarJobFailureModalHost';
import { SarJobResultModalHost } from '@/features/analytics/components/SarJobResultModalHost';
import { SessionRefreshScheduler } from '@/features/auth/components/SessionRefreshScheduler';
import { setCredentials } from '@/features/auth/stores/authSlice';
import { shouldAttemptSilentSessionRestore } from '@/features/auth/utils/sessionProbe';
import { trySilentSessionRestore } from '@/features/auth/utils/silentSessionRestore';
import { getToken, isTokenExpired } from '@/features/auth/utils/token';
import { getAntdThemeConfig } from '@/lib/antd-theme';
import { store } from '@/stores';

import { router } from './router';

function AuthBootstrap({ children }: { children: React.ReactNode }) {
  const [bootstrapped, setBootstrapped] = useState(() => {
    if (!env.API_BASE_URL) return true;
    const t = getToken();
    return !!(t && !isTokenExpired(t));
  });

  useEffect(() => {
    if (bootstrapped) return;
    let cancelled = false;
    (async () => {
      try {
        if (!shouldAttemptSilentSessionRestore()) return;
        const restored = await trySilentSessionRestore();
        if (!cancelled && restored) {
          store.dispatch(setCredentials(restored));
        }
      } finally {
        if (!cancelled) setBootstrapped(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [bootstrapped]);

  if (!bootstrapped) return <PageLoading />;
  return (
    <>
      <SessionRefreshScheduler />
      {children}
    </>
  );
}

export function AppProvider() {
  return (
    <ErrorBoundary>
      <ColorSchemeProvider>
        <AppProviderInner />
      </ColorSchemeProvider>
    </ErrorBoundary>
  );
}

function AppProviderInner() {
  const { i18n } = useTranslation();
  const { resolved: colorScheme } = useColorScheme();
  const [language, setLanguage] = useState(() => i18n.resolvedLanguage ?? i18n.language);

  useEffect(() => {
    const handler = (lng: string) => setLanguage(lng);
    i18n.on('languageChanged', handler);
    return () => {
      i18n.off('languageChanged', handler);
    };
  }, [i18n]);

  const antdLocale = useMemo(() => (language === 'en' ? enUS : viVN), [language]);
  const antdTheme = useMemo(() => getAntdThemeConfig(colorScheme), [colorScheme]);

  return (
    <ConfigProvider locale={antdLocale} theme={antdTheme}>
      <AntApp>
        <AuthBootstrap>
          <>
            <RouterProvider router={router} />
            <MapSarJobFailureModalHost />
            <SarJobResultModalHost />
          </>
        </AuthBootstrap>
      </AntApp>
    </ConfigProvider>
  );
}
